'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function HeroSection() {
  const bgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => {
      if (bgRef?.current) {
        bgRef.current.style.transform = `translateY(${window.scrollY * 0.3}px)`;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Parallax background */}
      <div className="absolute inset-0 z-0 scale-110" ref={bgRef}>
        <AppImage
          src="https://images.unsplash.com/photo-1654183631495-2d6f3d1affda"
          alt="Large luxury Texas home exterior with manicured lawn on a sunny summer day"
          fill
          priority
          className="object-cover object-center" />
        
        {/* Multi-layer overlay for depth */}
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(9,22,40,0.68)_0%,rgba(9,22,40,0.38)_45%,rgba(9,22,40,0.85)_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(9,22,40,0.6)_0%,transparent_60%)]" />
      </div>
      {/* Atmospheric glow blobs */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-amber/10 blur-[100px] pointer-events-none z-0" />
      <div className="absolute bottom-1/3 left-1/3 w-72 h-72 rounded-full bg-navy-light/20 blur-[80px] pointer-events-none z-0" />
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pt-28 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">

          {/* Left: headline + CTA */}
          <div className="lg:col-span-7 reveal-hidden" id="hero-text">
            {/* Trust pill */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card text-xs font-semibold text-amber mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '0.04em' }}>
              <span className="w-2 h-2 rounded-full bg-amber animate-pulse" />
              TDLR-Licensed · Dallas–Fort Worth
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              5 Ton Trane AC Systems<br />
              <span className="text-amber">Built for Large</span><br />
              Texas Homes
            </h1>

            <p className="text-lg text-white/75 font-light leading-relaxed max-w-xl mb-8" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              Powerful cooling. Energy-efficient performance. Professionally installed by Trane specialists who know Texas heat — not just HVAC basics.
            </p>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <a href="https://www.hvacservicespro.com" target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-xl text-base">
                Get My Free Estimate
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
              <a href="https://www.hvacservicespro.com" target="_blank" rel="noopener noreferrer" className="btn-secondary inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-xl text-base">
                Check Pricing Options
              </a>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-3">
              {[
              { icon: '🏆', label: 'Trane Specialists' },
              { icon: '📋', label: 'TDLR Licensed' },
              { icon: '🔒', label: '10-Year Warranty' },
              { icon: '💳', label: 'Financing Available' }]?.
              map((b) =>
              <div key={b?.label} className="trust-badge flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white/90" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  <span>{b?.icon}</span>
                  <span>{b?.label}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right: floating stat cards */}
          <div className="lg:col-span-5 flex flex-col gap-4 reveal-hidden reveal-delay-2" id="hero-stats">
            {[
            {
              label: 'Average Home Size Served',
              value: '2,500–3,500',
              unit: 'sq ft',
              icon: '🏠',
              sub: 'Ideal range for 5-ton systems in Texas'
            },
            {
              label: 'Texas Cooling Season',
              value: '8+',
              unit: 'months',
              icon: '☀️',
              sub: 'Dallas summers regularly hit 95–100°F'
            },
            {
              label: 'Potential Energy Savings',
              value: 'Up to 30%',
              unit: '',
              icon: '⚡',
              sub: 'vs. 10 SEER unit — upgrading to 16 SEER2'
            }]?.
            map((s, i) =>
            <div key={i} className="glass-card rounded-2xl p-5 flex items-start gap-4 animate-float" style={{ animationDelay: `${i * 1.5}s` }}>
                <div className="w-10 h-10 rounded-xl bg-amber/20 flex items-center justify-center text-lg flex-shrink-0">
                  {s?.icon}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.12em] text-white/55 font-semibold mb-0.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {s?.label}
                  </div>
                  <div className="text-2xl font-bold text-white leading-none mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {s?.value}<span className="text-base font-medium text-amber ml-1">{s?.unit}</span>
                  </div>
                  <div className="text-xs text-white/55 font-light">{s?.sub}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 text-white/40">
        <span className="text-[10px] uppercase tracking-widest font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </section>);

}